import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList
} from 'recharts';
import { FiTrendingUp, FiHeart, FiMessageCircle, FiUsers, FiGrid, FiClock } from 'react-icons/fi';
import Select from 'react-select';
import api from '../services/api';
import Loading from './Loading';
import './InstagramAnalyticsChart.css';

const InstagramAnalyticsChart = ({ accounts }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [postsData, setPostsData] = useState(null);

  // ✅ Nuevo modo: post_history
  const [viewMode, setViewMode] = useState('account'); // 'account' | 'posts' | 'post_history'

  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('followers');
  const [postMetric, setPostMetric] = useState('engagement'); // Para vista de posts
  const [timeRange, setTimeRange] = useState(30);
  const [loading, setLoading] = useState(false);

  // ✅ Estados para historial de posts
  const [selectedAccountsHistory, setSelectedAccountsHistory] = useState([]); // array de account_id (string)
  const [availableHistoryPosts, setAvailableHistoryPosts] = useState([]); // posts disponibles según cuentas
  const [selectedHistoryPosts, setSelectedHistoryPosts] = useState([]); // array de "accountId|postId"
  const [historyMetric, setHistoryMetric] = useState('engagement'); // engagement | likes | comments
  const [historyInterval, setHistoryInterval] = useState('1day'); // 10min | 30min | 1hour | 3hours | 6hours | 12hours | 1day
  const [historyData, setHistoryData] = useState(null);

  useEffect(() => {
    if (viewMode === 'account') {
      loadAnalytics();
    } else if (viewMode === 'posts') {
      loadPostsAnalytics();
    }
  }, [selectedAccount, timeRange, viewMode]);

  // ============ API Helpers ============
  const getApiBase = () => {
    const baseURL = api?.defaults?.baseURL || '/api/content';
    return baseURL.replace(/\/api\/content\/?$/i, '/instagram/api');
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const API_BASE_URL = getApiBase();
      const params = new URLSearchParams({ days: timeRange });

      if (selectedAccount !== 'all') {
        params.append('account_id', selectedAccount);
      }

      const response = await api.get(`${API_BASE_URL}/tracked-accounts/analytics/?${params}`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error al cargar analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPostsAnalytics = async () => {
    setLoading(true);
    try {
      const API_BASE_URL = getApiBase();
      const params = new URLSearchParams({ days: timeRange });

      if (selectedAccount !== 'all') {
        params.append('account_id', selectedAccount);
      }

      const response = await api.get(`${API_BASE_URL}/tracked-accounts/post_analytics/?${params}`);
      setPostsData(response.data);
    } catch (error) {
      console.error('Error al cargar posts analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Cargar posts disponibles para historial según cuentas seleccionadas (multi)
  const loadAvailablePostsForHistory = async () => {
    setLoading(true);
    try {
      const API_BASE_URL = getApiBase();

      if (!selectedAccountsHistory.length) {
        setAvailableHistoryPosts([]);
        setSelectedHistoryPosts([]);
        setHistoryData(null);
        return;
      }

      const responses = await Promise.all(
        selectedAccountsHistory.map((accountId) =>
          api.get(`${API_BASE_URL}/tracked-accounts/post_analytics/?account_id=${accountId}&days=${timeRange}`)
        )
      );

      const merged = responses.flatMap((r) => {
        const acc = r.data.account;
        const posts = r.data.posts || [];
        return posts.map((p) => ({
          account_id: String(acc.id),
          username: acc.username,
          post_id: p.post_id,
          caption: p.caption,
          permalink: p.permalink,
          date: p.date,
        }));
      });

      setAvailableHistoryPosts(merged);

      setSelectedHistoryPosts((prev) =>
        prev.filter((key) => {
          const [aid, pid] = key.split('|');
          return merged.some((p) => p.account_id === aid && p.post_id === pid);
        })
      );
    } catch (error) {
      console.error('Error al cargar posts disponibles para historial:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Llamar endpoint post_history con selección de posts e intervalo
  const loadHistorySeries = async () => {
    try {
      const API_BASE_URL = getApiBase();

      if (!selectedHistoryPosts.length) {
        setHistoryData(null);
        return;
      }

      const accountIds = Array.from(new Set(selectedHistoryPosts.map((v) => v.split('|')[0]))).join(',');
      const postIds = selectedHistoryPosts.map((v) => v.split('|')[1]).join(',');

      const params = new URLSearchParams({
        account_ids: accountIds,
        post_ids: postIds,
        days: String(timeRange),
        interval: historyInterval,
      });

      const res = await api.get(`${API_BASE_URL}/tracked-accounts/post_history/?${params}`);
      setHistoryData(res.data);
    } catch (error) {
      console.error('Error al cargar historial de posts:', error);
    }
  };

  useEffect(() => {
    if (viewMode !== 'post_history') return;

    if (!selectedAccountsHistory.length && accounts?.length) {
      setSelectedAccountsHistory([String(accounts[0].id)]);
    }
    // eslint-disable-next-line
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== 'post_history') return;
    loadAvailablePostsForHistory();
    // eslint-disable-next-line
  }, [selectedAccountsHistory, timeRange, viewMode]);

  useEffect(() => {
    if (viewMode !== 'post_history') return;
    loadHistorySeries();
    // eslint-disable-next-line
  }, [selectedHistoryPosts, timeRange, historyInterval, viewMode]);

  // ============ Formatters ============
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', { month: 'short', day: 'numeric' });
  };

  const formatDateWithTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Formatter dinámico para el eje X según el intervalo seleccionado
  const formatXAxisByInterval = (dateString) => {
    const date = new Date(dateString);

    switch(historyInterval) {
      case '10min':
      case '30min':
      case '1hour':
        // Para intervalos cortos: mostrar hora:minutos
        return date.toLocaleTimeString('es-CL', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });

      case '3hours':
      case '6hours':
        // Para intervalos medios: mostrar día y hora
        return date.toLocaleDateString('es-CL', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });

      case '12hours':
      case '1day':
        // Para intervalos largos: mostrar día y mes
        return date.toLocaleDateString('es-CL', {
          day: 'numeric',
          month: 'short'
        });

      default:
        return formatDate(dateString);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString('es-CL');
  };

  const getAccountColors = () => {
    const colors = ['#102826', '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];
    return colors;
  };

  const colors = getAccountColors();

  // Helper: hex -> rgba para distinguir posts de la misma cuenta por opacidad
  const hexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(16,40,38,${alpha})`;
    const h = hex.replace('#', '').trim();
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  // ============ Account charts logic (NO CAMBIA) ============
  const getChartData = () => {
    if (!analyticsData) return [];

    if (selectedAccount === 'all') {
      const allSnapshots = {};

      analyticsData.accounts?.forEach((account) => {
        account.snapshots.forEach((snapshot) => {
          const date = snapshot.date;
          if (!allSnapshots[date]) allSnapshots[date] = { date };
          allSnapshots[date][account.username] = snapshot[selectedMetric];
        });
      });

      return Object.values(allSnapshots).sort((a, b) => new Date(a.date) - new Date(b.date));
    } else {
      return (
        analyticsData.snapshots?.map((s) => ({
          date: s.date,
          value: s[selectedMetric],
        })) || []
      );
    }
  };

  const getLatestStats = () => {
    if (!analyticsData) return null;

    if (selectedAccount === 'all') {
      return analyticsData.accounts?.map((account, idx) => {
        const latest = account.snapshots[account.snapshots.length - 1];
        const previous = account.snapshots[account.snapshots.length - 2];
        const change = previous
          ? ((latest[selectedMetric] - previous[selectedMetric]) / previous[selectedMetric] * 100).toFixed(1)
          : 0;

        return {
          username: account.username,
          value: latest[selectedMetric],
          change: change,
          color: colors[idx % colors.length],
        };
      });
    } else {
      if (!analyticsData.account) return null;

      const snapshots = analyticsData.snapshots || [];
      if (snapshots.length === 0) return null;

      const latest = snapshots[snapshots.length - 1];
      const previous = snapshots[snapshots.length - 2];
      const change = previous
        ? ((latest[selectedMetric] - previous[selectedMetric]) / previous[selectedMetric] * 100).toFixed(1)
        : 0;

      return [
        {
          username: analyticsData.account.username,
          value: latest[selectedMetric],
          change: change,
          color: '#102826',
        },
      ];
    }
  };

  const chartData = viewMode === 'account' ? getChartData() : [];
  const stats = viewMode === 'account' ? getLatestStats() : null;

  const metrics = [
    { id: 'followers', label: 'Seguidores', icon: FiUsers },
    { id: 'total_likes', label: 'Likes Totales', icon: FiHeart },
    { id: 'total_comments', label: 'Comentarios', icon: FiMessageCircle },
    { id: 'engagement_rate', label: 'Engagement Rate', icon: FiTrendingUp },
  ];

  const postMetrics = [
    { id: 'engagement', label: 'Engagement Total', icon: FiTrendingUp },
    { id: 'likes', label: 'Likes', icon: FiHeart },
    { id: 'comments', label: 'Comentarios', icon: FiMessageCircle },
  ];

  const historyMetrics = [
    { id: 'engagement', label: 'Engagement Diario', icon: FiTrendingUp },
    { id: 'likes', label: 'Likes Diarios', icon: FiHeart },
    { id: 'comments', label: 'Comentarios Diarios', icon: FiMessageCircle },
  ];

  // ============ POSTS (stacked): leyenda por cuenta + tooltip por post ============
  const MAX_STACKED_POSTS = 12;

  const stackedPosts = useMemo(() => {
    const rawPosts = postsData?.posts || [];
    if (!rawPosts.length) return { data: [], series: [], legendUsers: [] };

    const top = rawPosts.slice(0, MAX_STACKED_POSTS);

    // Mapeo username -> color base (estable)
    const usernameList = Array.from(new Set(top.map((p) => p.account || postsData?.account?.username || 'post')));
    const userColorMap = {};
    usernameList.forEach((u, idx) => {
      userColorMap[u] = colors[idx % colors.length];
    });

    // Para diferenciar múltiples posts del mismo user: alpha por orden
    const perUserCount = {};
    const perUserIndex = {};

    // Datos por día
    const byDay = {};
    const series = []; // [{ key, username, label, fill, ... }]
    const legendUsers = usernameList.map((u) => ({ username: u, color: userColorMap[u] }));

    // Pre-contar posts por user en el top
    usernameList.forEach((u) => { perUserCount[u] = 0; perUserIndex[u] = 0; });
    top.forEach((p) => {
      const username = p.account || postsData?.account?.username || 'post';
      perUserCount[username] += 1;
    });

    const alphaFor = (username) => {
      // alpha decreciente si hay varios posts del mismo user
      const idx = perUserIndex[username] || 0;
      const total = perUserCount[username] || 1;
      // alpha entre 0.35 y 1.0
      const maxA = 1.0;
      const minA = 0.35;
      if (total <= 1) return maxA;
      const t = idx / (total - 1);
      return maxA - (maxA - minA) * t;
    };

    top.forEach((p) => {
      const day = p.date ? p.date.split('T')[0] : 'sin_fecha';
      const username = p.account || postsData?.account?.username || 'post';

      const key = `${username}|${p.post_id}`; // único por post
      const label = `@${username}`; // 👈 solo username (para el tooltip/metadata)
      const baseColor = userColorMap[username] || '#102826';

      const a = alphaFor(username);
      const fill = hexToRgba(baseColor, a);

      // avanzar índice por user para alpha
      perUserIndex[username] = (perUserIndex[username] || 0) + 1;

      if (!series.some((s) => s.key === key)) {
        series.push({
          key,
          username,
          label,
          post_id: p.post_id,
          caption: p.caption,
          permalink: p.permalink,
          fill,
          baseColor,
        });
      }

      if (!byDay[day]) byDay[day] = { date: day, __meta: {} };

      const value = p[postMetric] ?? 0;
      byDay[day][key] = (byDay[day][key] || 0) + value;

      // guardar meta para tooltip (para este día + post)
      byDay[day].__meta[key] = {
        username,
        post_id: p.post_id,
        caption: p.caption,
        permalink: p.permalink,
        value,
      };
    });

    const data = Object.values(byDay).sort((a, b) => new Date(a.date) - new Date(b.date));
    return { data, series, legendUsers };
  }, [postsData, postMetric, colors]); // colors es estable por función

  // Legend custom: mostrar 1 vez por username
  const StackedLegend = ({ legendUsers }) => {
    if (!legendUsers?.length) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '0 8px' }}>
        {legendUsers.map((u) => (
          <div key={u.username} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
            <span style={{ width: 10, height: 10, borderRadius: 999, background: u.color, display: 'inline-block' }} />
            <span>@{u.username}</span>
          </div>
        ))}
      </div>
    );
  };

  // Tooltip custom: lista todos los posts/segmentos del día con caption/links
  const StackedPostsTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    // payload contiene entradas por Bar => necesitamos meta desde el row
    const row = payload[0]?.payload;
    const meta = row?.__meta || {};

    // Filtrar solo los keys con value > 0 y ordenarlos desc por valor
    const items = payload
      .map((p) => {
        const key = p.dataKey;
        const m = meta[key];
        if (!m) return null;
        return {
          key,
          username: m.username,
          post_id: m.post_id,
          caption: m.caption,
          permalink: m.permalink,
          value: m.value,
          color: p.color || p.fill,
        };
      })
      .filter(Boolean)
      .filter((i) => (i.value ?? 0) > 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    return (
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          maxWidth: 320,
        }}
      >
        <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#102826' }}>
          {formatDate(label)}
        </p>

        {items.map((it) => (
          <div key={it.key} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px dashed #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: it.color, display: 'inline-block' }} />
              <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: '#111827' }}>
                @{it.username}
              </p>
            </div>

            {it.caption && (
              <p style={{ margin: '6px 0', fontSize: 12, color: '#6b7280' }}>
                {it.caption}
              </p>
            )}

            <p style={{ margin: '4px 0', fontSize: 13 }}>
              <strong>
                {postMetric === 'engagement' ? 'Engagement' : postMetric === 'likes' ? 'Likes' : 'Comentarios'}:
              </strong>{' '}
              {formatNumber(it.value)}
            </p>

            <p style={{ margin: '4px 0', fontSize: 11, color: '#9ca3af' }}>
              <strong>Post ID:</strong> {it.post_id}
            </p>

            {it.permalink && (
              <a
                href={it.permalink}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-block',
                  marginTop: 6,
                  color: '#102826',
                  fontSize: 12,
                  textDecoration: 'none',
                  borderBottom: '1px solid #102826',
                }}
              >
                Ver post en Instagram →
              </a>
            )}
          </div>
        ))}

        {items.length === 0 && (
          <div style={{ fontSize: 12, color: '#6b7280' }}>Sin datos para este día.</div>
        )}
      </div>
    );
  };

  // ============ Historial: construir data para recharts con metadata de crecimiento ============
  const historyChartData = useMemo(() => {
    if (!historyData?.series?.length) return [];

    const byDate = {};

    // Primero, calcular crecimiento para cada serie
    const growthData = {};
    historyData.series.forEach((s) => {
      const sortedPoints = [...(s.points || [])].sort((a, b) => new Date(a.date) - new Date(b.date));

      sortedPoints.forEach((pt, idx) => {
        const currentValue = pt[historyMetric] ?? 0;
        let growth = null;
        let growthPercent = null;

        if (idx > 0) {
          const prevValue = sortedPoints[idx - 1][historyMetric] ?? 0;
          growth = currentValue - prevValue;
          growthPercent = prevValue > 0 ? ((growth / prevValue) * 100) : (currentValue > 0 ? 100 : 0);
        }

        if (!growthData[pt.date]) growthData[pt.date] = {};
        growthData[pt.date][s.series_key] = {
          value: currentValue,
          growth,
          growthPercent,
          username: s.username,
          post_id: s.post_id,
          caption: s.caption,
        };
      });
    });

    // Construir el objeto final con valores y metadata
    historyData.series.forEach((s) => {
      (s.points || []).forEach((pt) => {
        if (!byDate[pt.date]) byDate[pt.date] = { date: pt.date, __meta: {} };
        byDate[pt.date][s.series_key] = pt[historyMetric];
        byDate[pt.date].__meta[s.series_key] = growthData[pt.date]?.[s.series_key] || {};
      });
    });

    return Object.values(byDate).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [historyData, historyMetric]);

  // ✅ NUEVO: leyenda por username (igual a Posts) + líneas por post con opacidad
  const historyStyled = useMemo(() => {
    const series = historyData?.series || [];
    if (!series.length) return { legendUsers: [], styledSeries: [] };

    // usernames únicos para color estable
    const usernameList = Array.from(new Set(series.map(s => s.username)));
    const userColorMap = {};
    usernameList.forEach((u, idx) => {
      userColorMap[u] = colors[idx % colors.length];
    });

    // contar posts por user para opacidad
    const perUserCount = {};
    const perUserIndex = {};
    usernameList.forEach((u) => { perUserCount[u] = 0; perUserIndex[u] = 0; });
    series.forEach((s) => { perUserCount[s.username] = (perUserCount[s.username] || 0) + 1; });

    const alphaFor = (username) => {
      const idx = perUserIndex[username] || 0;
      const total = perUserCount[username] || 1;
      const maxA = 1.0;
      const minA = 0.35;
      if (total <= 1) return maxA;
      const t = idx / (total - 1);
      return maxA - (maxA - minA) * t;
    };

    const styledSeries = series.map((s) => {
      const baseColor = userColorMap[s.username] || '#102826';
      const a = alphaFor(s.username);
      perUserIndex[s.username] = (perUserIndex[s.username] || 0) + 1;

      return {
        ...s,
        baseColor,
        stroke: hexToRgba(baseColor, a),
      };
    });

    const legendUsers = usernameList.map((u) => ({ username: u, color: userColorMap[u] }));

    return { legendUsers, styledSeries };
  }, [historyData, colors]);

  const HistoryLegend = ({ legendUsers }) => {
    if (!legendUsers?.length) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '0 8px' }}>
        {legendUsers.map((u) => (
          <div key={u.username} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
            <span style={{ width: 10, height: 10, borderRadius: 999, background: u.color, display: 'inline-block' }} />
            <span>@{u.username}</span>
          </div>
        ))}
      </div>
    );
  };

  // Tooltip personalizado para mostrar crecimiento entre puntos
  const HistoryTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const row = payload[0]?.payload;
    const meta = row?.__meta || {};

    // Filtrar items con valores y ordenar por valor descendente
    const items = payload
      .map((p) => {
        const key = p.dataKey;
        const m = meta[key];
        if (!m || m.value === undefined) return null;

        return {
          key,
          username: m.username,
          post_id: m.post_id,
          caption: m.caption,
          value: m.value,
          growth: m.growth,
          growthPercent: m.growthPercent,
          color: p.stroke || p.color,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    if (!items.length) return null;

    const metricLabel = historyMetric === 'likes' ? 'Likes' : historyMetric === 'comments' ? 'Comentarios' : 'Engagement';

    return (
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          minWidth: '250px',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '13px', color: '#374151' }}>
          {formatXAxisByInterval(label)}
        </div>

        {items.map((item) => {
          const showGrowth = item.growth !== null && item.growth !== undefined;
          const isPositive = item.growth > 0;
          const isNegative = item.growth < 0;
          const growthColor = isPositive ? '#10b981' : isNegative ? '#ef4444' : '#6b7280';
          const growthIcon = isPositive ? '↑' : isNegative ? '↓' : '•';

          return (
            <div
              key={item.key}
              style={{
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid #f3f4f6',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                  @{item.username}
                </span>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                  ...{item.post_id?.slice(-6) || ''}
                </span>
              </div>

              <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '2px' }}>
                {formatNumber(item.value)} {metricLabel}
              </div>

              {showGrowth && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                  <span style={{ color: growthColor, fontWeight: 600 }}>
                    {growthIcon} {isPositive ? '+' : ''}{formatNumber(item.growth)}
                  </span>
                  <span style={{ color: '#9ca3af' }}>
                    ({isPositive ? '+' : ''}{item.growthPercent?.toFixed(1)}%)
                  </span>
                </div>
              )}

              {!showGrowth && (
                <div style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>
                  Primer punto
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Label personalizado para mostrar porcentaje de crecimiento en cada punto
  const GrowthLabel = ({ x, y, value, index, seriesKey }) => {
    // Obtener la metadata del punto actual
    const dataPoint = historyChartData[index];
    if (!dataPoint || !dataPoint.__meta) return null;

    const meta = dataPoint.__meta[seriesKey];
    if (!meta || meta.growthPercent === null || meta.growthPercent === undefined) return null;

    const isPositive = meta.growthPercent > 0;
    const isNegative = meta.growthPercent < 0;
    const color = isPositive ? '#10b981' : isNegative ? '#ef4444' : '#6b7280';
    const sign = isPositive ? '+' : '';

    return (
      <text
        x={x}
        y={y - 12}
        textAnchor="middle"
        fill={color}
        fontSize="10"
        fontWeight="600"
      >
        {sign}{meta.growthPercent.toFixed(1)}%
      </text>
    );
  };

  // ============ Loading ============
  if (loading) {
    return <Loading fullScreen={false} />;
  }

  // Validación para vista de account (NO CAMBIA)
  if (viewMode === 'account') {
    if (!analyticsData) return null;
    if (chartData.length === 0) {
      return (
        <div className="analytics-empty">
          <FiTrendingUp size={48} />
          <h3>No hay datos históricos disponibles</h3>
          <p>Guarda métricas primero para ver las gráficas</p>
        </div>
      );
    }
  }

  // Validación para vista de posts (NO CAMBIA)
  if (viewMode === 'posts' && (!postsData || !postsData.posts || postsData.posts.length === 0)) {
    return (
      <div className="analytics-empty">
        <FiGrid size={48} />
        <h3>No hay posts disponibles</h3>
        <p>Actualiza las cuentas para obtener datos de posts</p>
      </div>
    );
  }

  // Validación para vista de historial
  if (
    viewMode === 'post_history' &&
    selectedHistoryPosts.length > 0 &&
    (!historyData || !historyData.series || historyData.series.length === 0)
  ) {
    return (
      <div className="analytics-empty">
        <FiClock size={48} />
        <h3>No hay historial para los posts seleccionados</h3>
        <p>Verifica que existan snapshots diarios (snapshot_bucket) para esos posts.</p>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div className="header-title-section">
          <h2>📊 Analytics</h2>
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'account' ? 'active' : ''}`}
              onClick={() => setViewMode('account')}
            >
              <FiTrendingUp size={16} />
              Cuentas
            </button>
            <button
              className={`toggle-btn ${viewMode === 'posts' ? 'active' : ''}`}
              onClick={() => setViewMode('posts')}
            >
              <FiGrid size={16} />
              Posts
            </button>
            <button
              className={`toggle-btn ${viewMode === 'post_history' ? 'active' : ''}`}
              onClick={() => setViewMode('post_history')}
              title="Historial diario por post"
            >
              <FiClock size={16} />
              Historial Posts
            </button>
          </div>
        </div>

        <div className="analytics-controls">
          {(viewMode === 'account' || viewMode === 'posts') && (
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="analytics-select"
            >
              <option value="all">Todas las cuentas</option>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  @{acc.username}
                </option>
              ))}
            </select>
          )}

          {viewMode === 'post_history' && (
            <Select
              isMulti
              options={accounts.map((acc) => ({
                value: String(acc.id),
                label: `@${acc.username}`
              }))}
              value={accounts
                .filter((acc) => selectedAccountsHistory.includes(String(acc.id)))
                .map((acc) => ({
                  value: String(acc.id),
                  label: `@${acc.username}`
                }))}
              onChange={(selectedOptions) => {
                const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
                setSelectedAccountsHistory(values);
              }}
              placeholder="Selecciona una o más cuentas"
              className="analytics-select"
              classNamePrefix="select"
              isClearable
            />
          )}

          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="analytics-select"
          >
            <option value={1}>Último día</option>
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
        </div>
      </div>

      {/* Selector de métricas */}
      {viewMode === 'account' ? (
        <div className="metrics-selector">
          {metrics.map((metric) => (
            <button
              key={metric.id}
              className={`metric-btn ${selectedMetric === metric.id ? 'active' : ''}`}
              onClick={() => setSelectedMetric(metric.id)}
            >
              <metric.icon size={18} />
              {metric.label}
            </button>
          ))}
        </div>
      ) : viewMode === 'posts' ? (
        <div className="metrics-selector">
          {postMetrics.map((metric) => (
            <button
              key={metric.id}
              className={`metric-btn ${postMetric === metric.id ? 'active' : ''}`}
              onClick={() => setPostMetric(metric.id)}
            >
              <metric.icon size={18} />
              {metric.label}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="metrics-selector">
            {historyMetrics.map((metric) => (
              <button
                key={metric.id}
                className={`metric-btn ${historyMetric === metric.id ? 'active' : ''}`}
                onClick={() => setHistoryMetric(metric.id)}
              >
                <metric.icon size={18} />
                {metric.label}
              </button>
            ))}
          </div>

          {/* Selector de intervalo de tiempo */}
          <div className="metrics-selector" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
            <button
              className={`metric-btn ${historyInterval === '10min' ? 'active' : ''}`}
              onClick={() => setHistoryInterval('10min')}
            >
              ⏱️ 10 min
            </button>
            <button
              className={`metric-btn ${historyInterval === '30min' ? 'active' : ''}`}
              onClick={() => setHistoryInterval('30min')}
            >
              ⏱️ 30 min
            </button>
            <button
              className={`metric-btn ${historyInterval === '1hour' ? 'active' : ''}`}
              onClick={() => setHistoryInterval('1hour')}
            >
              🕐 1 hora
            </button>
            <button
              className={`metric-btn ${historyInterval === '3hours' ? 'active' : ''}`}
              onClick={() => setHistoryInterval('3hours')}
            >
              🕒 3 horas
            </button>
            <button
              className={`metric-btn ${historyInterval === '6hours' ? 'active' : ''}`}
              onClick={() => setHistoryInterval('6hours')}
            >
              🕕 6 horas
            </button>
            <button
              className={`metric-btn ${historyInterval === '12hours' ? 'active' : ''}`}
              onClick={() => setHistoryInterval('12hours')}
            >
              🕛 12 horas
            </button>
            <button
              className={`metric-btn ${historyInterval === '1day' ? 'active' : ''}`}
              onClick={() => setHistoryInterval('1day')}
            >
              📅 1 día
            </button>
          </div>
        </>
      )}

      {/* Stats overview (NO CAMBIA) */}
      {viewMode === 'account' && stats && (
        <div className="stats-overview">
          {stats.map((stat, idx) => (
            <div key={idx} className="stat-overview-card" style={{ borderLeftColor: stat.color }}>
              <div className="stat-overview-header">
                <span className="stat-overview-username">@{stat.username}</span>
                <span className={`stat-overview-change ${parseFloat(stat.change) >= 0 ? 'positive' : 'negative'}`}>
                  {parseFloat(stat.change) >= 0 ? '+' : ''}
                  {stat.change}%
                </span>
              </div>
              <div className="stat-overview-value">{formatNumber(stat.value)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Controles extra para historial (selector de posts) */}
      {viewMode === 'post_history' && (
        <div style={{ marginTop: 10, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 420px' }}>
              <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                Posts (selecciona uno o más)
              </label>
              <Select
                isMulti
                options={availableHistoryPosts.map((p) => {
                  const value = `${p.account_id}|${p.post_id}`;
                  const label = `@${p.username} — ${p.caption || p.post_id}`;
                  return { value, label };
                })}
                value={availableHistoryPosts
                  .filter((p) => selectedHistoryPosts.includes(`${p.account_id}|${p.post_id}`))
                  .map((p) => {
                    const value = `${p.account_id}|${p.post_id}`;
                    const label = `@${p.username} — ${p.caption || p.post_id}`;
                    return { value, label };
                  })}
                onChange={(selectedOptions) => {
                  const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
                  setSelectedHistoryPosts(values);
                }}
                placeholder="Selecciona uno o más posts"
                className="analytics-select"
                classNamePrefix="select"
                isClearable
                styles={{
                  container: (provided) => ({
                    ...provided,
                    width: '100%'
                  }),
                  control: (provided) => ({
                    ...provided,
                    minHeight: 40
                  })
                }}
              />

              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
                Selecciona múltiples posts para comparar su evolución.
              </div>
            </div>

            <div style={{ flex: '0 0 240px' }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Acciones rápidas</div>

              <button
                className="toggle-btn"
                style={{ width: '100%', justifyContent: 'center', marginBottom: 8 }}
                onClick={() => {
                  const first = availableHistoryPosts.slice(0, 3).map((p) => `${p.account_id}|${p.post_id}`);
                  setSelectedHistoryPosts(first);
                }}
                disabled={!availableHistoryPosts.length}
              >
                Seleccionar Top 3
              </button>

              <button
                className="toggle-btn"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => setSelectedHistoryPosts([])}
                disabled={!selectedHistoryPosts.length}
              >
                Limpiar selección
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="chart-container">
        <ResponsiveContainer width="100%" height={400}>
          {viewMode === 'posts' ? (
            <BarChart data={stackedPosts.data} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => formatDate(d)}
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tickFormatter={formatNumber} stroke="#6b7280" style={{ fontSize: '12px' }} />

              {/* ✅ Tooltip sigue mostrando cada post como antes */}
              <Tooltip content={<StackedPostsTooltip />} />

              {/* ✅ Legend por username y en la parte de abajo (como pediste antes) */}
              <Legend
                verticalAlign="bottom"
                align="center"
                content={() => <StackedLegend legendUsers={stackedPosts.legendUsers} />}
              />

              {stackedPosts.series.map((s) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={`@${s.username}`}
                  stackId="posts"
                  fill={s.fill}
                  radius={[6, 6, 0, 0]}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          ) : viewMode === 'post_history' ? (
            <LineChart data={historyChartData} margin={{ top: 30, right: 30, left: 20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => formatXAxisByInterval(d)}
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                angle={historyInterval === '10min' || historyInterval === '30min' ? 0 : -45}
                textAnchor={historyInterval === '10min' || historyInterval === '30min' ? 'middle' : 'end'}
              />
              <YAxis tickFormatter={formatNumber} stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip content={<HistoryTooltip />} />

              {/* ✅ NUEVO: Legend por username abajo */}
              <Legend
                verticalAlign="bottom"
                align="center"
                content={() => <HistoryLegend legendUsers={historyStyled.legendUsers} />}
              />

              {/* ✅ NUEVO: líneas por post con color base por username + opacidad para diferenciar múltiples posts */}
              {historyStyled.styledSeries.map((s) => (
                <Line
                  key={s.series_key}
                  type="monotone"
                  dataKey={s.series_key}
                  name={`@${s.username} • ${s.post_id?.slice(-6) || ''}`}
                  stroke={s.stroke}
                  strokeWidth={2}
                  dot={{ r: 4, fill: s.stroke, strokeWidth: 0 }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                  connectNulls
                >
                  <LabelList
                    content={(props) => <GrowthLabel {...props} seriesKey={s.series_key} />}
                  />
                </Line>
              ))}
            </LineChart>
          ) : selectedAccount === 'all' ? (
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={formatDate} stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis tickFormatter={formatNumber} stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip
                labelFormatter={formatDate}
                formatter={(value) => formatNumber(value)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                }}
              />
              <Legend />
              {analyticsData.accounts?.map((account, idx) => (
                <Line
                  key={account.username}
                  type="monotone"
                  dataKey={account.username}
                  stroke={colors[idx % colors.length]}
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6, fill: colors[idx % colors.length] }}
                  connectNulls={true}
                />
              ))}
            </LineChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#102826" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#102826" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={formatDate} stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis tickFormatter={formatNumber} stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip
                labelFormatter={formatDate}
                formatter={(value) => formatNumber(value)}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#102826"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6, fill: '#102826' }}
                fill="url(#colorValue)"
                fillOpacity={0.2}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {viewMode === 'posts' && (
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
          Barras apiladas por día. Leyenda por cuenta. Tooltip lista cada post. (Top {MAX_STACKED_POSTS} posts)
        </div>
      )}
    </div>
  );
};

export default InstagramAnalyticsChart;
